<script setup lang="ts">
/**
 * Email entry for login and signup. On signup, the UK taxpayer confirmation is
 * required; Continue stays clickable and highlights the checkbox if skipped.
 */
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/yup'
import { emailSchema } from '@/types'
import FieldError from '@/components/views/manage-account/FieldError.vue'
import AuthExplainerModal from '@/components/dashboard/modals/AuthExplainerModal.vue'
import { LoginMethod } from '@/types'
import type { Messages } from '@/i18n/en'

const props = defineProps({
  loginMethod: {
    type: String,
    required: false,
    default: LoginMethod.CODE,
  },
})

const store = useStore()
const { tm } = useI18n<{ message: Messages }>()

const validationSchema = toTypedSchema(emailSchema)
const { handleSubmit, errors, defineField } = useForm({
  validationSchema,
})
const [email] = defineField('email')

const ukTaxpayerConfirmed = ref(false)
const taxpayerModalOpen = ref(false)
/** Shown after Continue is pressed without confirming taxpayer status. */
const showTaxpayerError = ref(false)

const isSignup = computed(() => store.state.auth.authIntent === 'signup')
const btnLoading = computed(() => store.state.auth.btnLoading)
const referralEmail = computed(() => store.state.referralEmail)

const taxpayerParagraphs = computed(() => {
  const paragraphs = tm('ManageAccount.Signup.Start.TaxpayerModal.Paragraphs')
  return Array.isArray(paragraphs) ? (paragraphs as string[]) : []
})

const submitDescribedBy = computed(() =>
  showTaxpayerError.value ? 'signup-uk-taxpayer-hint' : undefined
)

watch(ukTaxpayerConfirmed, (confirmed) => {
  if (confirmed) {
    showTaxpayerError.value = false
  }
})

onMounted(() => {
  addReferralEmail()
})

const sendEmail = handleSubmit((values) => {
  // Block double-sends while Auth0 / mock passwordless start is in flight.
  if (btnLoading.value) return

  if (isSignup.value && !ukTaxpayerConfirmed.value) {
    showTaxpayerError.value = true
    return
  }

  store.dispatch('sendLoginEmail', {
    email: values.email,
    send: props.loginMethod,
  })
})

const addReferralEmail = () => {
  if (referralEmail.value) {
    email.value = referralEmail.value
  }
}
</script>

<template>
  <form class="sa-auth-form" novalidate @submit.prevent="sendEmail">
    <div
      class="sa-form-group"
      :class="{ 'sa-form-group--invalid': errors.email?.length }"
    >
      <label for="email" class="sa-form-label">
        {{ $t('ManageAccount.Login.EmailForm.FieldLabel') }}
      </label>

      <input
        id="email"
        v-model="email"
        class="sa-input"
        spellcheck="false"
        type="email"
        :class="{ 'is-invalid': errors.email?.length }"
        name="email"
        autocomplete="email"
        :aria-invalid="errors.email?.length ? true : undefined"
        :aria-describedby="errors.email?.length ? 'email-error' : undefined"
        :placeholder="$t('ManageAccount.Login.EmailForm.FieldPlaceholder')"
      />

      <FieldError
        :error="errors.email"
        error-id="email-error"
        class="sa-auth-form__error"
      />
    </div>

    <div
      v-if="isSignup"
      class="sa-auth-form__taxpayer"
      :class="{ 'sa-auth-form__taxpayer--error': showTaxpayerError }"
    >
      <div class="sa-auth-form__taxpayer-control">
        <input
          id="signup-uk-taxpayer"
          v-model="ukTaxpayerConfirmed"
          type="checkbox"
          class="sa-auth-form__taxpayer-checkbox"
          data-testid="signup-uk-taxpayer-checkbox"
          :aria-invalid="showTaxpayerError ? true : undefined"
          :aria-describedby="
            showTaxpayerError ? 'signup-uk-taxpayer-hint' : undefined
          "
        />
        <div class="sa-auth-form__taxpayer-copy">
          <label class="sa-auth-form__taxpayer-label" for="signup-uk-taxpayer">
            {{ $t('ManageAccount.Signup.Start.TaxpayerCheckbox') }}
          </label>
          {{ ' ' }}
          <button
            type="button"
            class="sa-auth-step__explain"
            data-testid="signup-uk-taxpayer-learn-more"
            @click="taxpayerModalOpen = true"
          >
            {{ $t('ManageAccount.Signup.Start.LearnMore') }}
          </button>
        </div>
      </div>
      <p
        v-if="showTaxpayerError"
        id="signup-uk-taxpayer-hint"
        class="sa-auth-form__taxpayer-hint"
        role="alert"
      >
        {{ $t('ManageAccount.Signup.Start.TaxpayerHint') }}
      </p>
    </div>

    <button
      type="submit"
      class="sa-btn-primary sa-auth-form__submit"
      :disabled="btnLoading"
      :aria-busy="btnLoading"
      :aria-describedby="submitDescribedBy"
    >
      <span
        v-if="btnLoading"
        class="spinner sa-btn-spinner"
        aria-hidden="true"
      />
      <span>{{ $t('ManageAccount.Login.EmailForm.Button.Text') }}</span>
    </button>
  </form>

  <AuthExplainerModal
    :is-open="taxpayerModalOpen"
    title-id="signup-uk-taxpayer-title"
    :title="$t('ManageAccount.Signup.Start.TaxpayerModal.Title')"
    :paragraphs="taxpayerParagraphs"
    @close="taxpayerModalOpen = false"
  />
</template>
